/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { AggregationInterval, mapAggregationInterval } from '../../../shared/entities/dataUsage';
import appTheme from '../../common/theme/appTheme.json';
import { DATA_USAGE_CHART_HEADING_HEIGHT, DATA_USAGE_CHART_HEIGHT } from '../DataUsageContainer';

interface DataUsageChartContainerProps {
  children?: React.ReactNode;
  width: number;
  header: string | ReactElement;
  aggregationInterval?: AggregationInterval;
  menuItemClickHandler?: (aggregationInterval: AggregationInterval) => void;
}

function DataUsageChartContainer({
  children,
  width,
  header,
  aggregationInterval,
  menuItemClickHandler,
}: DataUsageChartContainerProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (aggregationInterval: AggregationInterval) => {
    if (
      menuItemClickHandler &&
      (aggregationInterval === 'YEAR' ||
        aggregationInterval === 'MONTH' ||
        aggregationInterval === 'WEEK' ||
        aggregationInterval === 'DAY')
    ) {
      menuItemClickHandler(aggregationInterval);
    }
    setAnchorEl(null);
  };
  return (
    <div style={{boxShadow:"0px 5px 5px 0px rgba(0,0,0,0.25)"}}>
      <Box
        sx={{
          width: `${width + 16}px`,
          height: `${DATA_USAGE_CHART_HEIGHT}px`,
          backgroundColor: appTheme.primaryBackgroundColor,
          borderRadius: '8px',
        }}
        boxShadow={0}
      >
        {/* chart header */}
        <Box
          sx={{
            height: `${DATA_USAGE_CHART_HEADING_HEIGHT}px`,
            display: 'flex',
            alignItems: 'center',
            paddingX: '16px',
            boxSizing: 'border-box',
            backgroundColor: appTheme.primaryBackgroundColor,
            borderBottom: `2px solid ${appTheme.secondaryBackgroundColor}`,
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <ChartHeading>{header}</ChartHeading>
          </Box>
          {aggregationInterval && (
            <>
              <Box>
                <Button
                  variant="contained"
                  sx={{ backgroundColor: appTheme.mptPrimaryBackgroundColor, borderRadius: '5px', border: 'none' }}
                  id="basic-button"
                  aria-controls={open ? 'basic-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={handleClick}
                  endIcon={<KeyboardArrowDown />}
                >
                  {aggregationInterval ? mapAggregationInterval(aggregationInterval) : 'Select Interval'}
                </Button>
                <Menu
                  id="basic-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  MenuListProps={{
                    'aria-labelledby': 'basic-button',
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handleClose('MONTH');
                    }}
                  >
                    {mapAggregationInterval('MONTH')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleClose('WEEK');
                    }}
                  >
                    {mapAggregationInterval('WEEK')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleClose('DAY');
                    }}
                  >
                    {mapAggregationInterval('DAY')}
                  </MenuItem>
                </Menu>
              </Box>
            </>
          )}
        </Box>
        {children}
      </Box>
    </div>
  );
}

export default DataUsageChartContainer;

export const ChartHeading = styled.h1`
  font-family: ${appTheme.viasatFontFamilyRegular};
  color: ${appTheme.primaryTextColor};
  font-size: 20px;
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.2px;
  margin: 0px;

  @media print {
    color: black;
    margin: 0px;
  }
`;
